import { useCallback, useEffect, useMemo, useState } from "react";

type RowState = {
	selected: boolean;
	editing: boolean;
	disabled?: boolean;
};

type EditableListItem<T> = T & { id: string };

export type EditableListRow<T> = EditableListItem<T> & RowState;

// Raw items
function useDraftItem<T>(initialItems: T[]) {
	const [items, setItems] = useState<EditableListItem<T>[]>([]);

	// Sync state when initialItems changes
	useEffect(() => {
		setItems(
			initialItems.map((item) => ({
				...item,
				// Only generate a new ID if it doesn't have one
				id: (item as EditableListItem<T>).id || crypto.randomUUID(),
			})),
		);
	}, [initialItems]);

	const addItem = useCallback(
		(newItem: T, onCreated?: (item: EditableListItem<T>) => void) => {
			const itemWithPermanentId = { ...newItem, id: crypto.randomUUID() };
			const updated = [itemWithPermanentId, ...items]; // this naturally puts new items first

			setItems(updated);

			if (onCreated) onCreated(itemWithPermanentId);
			return itemWithPermanentId;
		},
		[items],
	);

	const updateItem = useCallback(
		(id: string, updates: Partial<T>) => {
			const updated = items.map((item) => (item.id === id ? { ...item, ...updates } : item));
			setItems(updated);
			return updated;
		},
		[items],
	);

	const deleteItem = useCallback(
		(id: string) => {
			const updated = items.filter((item) => item.id !== id);
			setItems(updated);
			return updated;
		},
		[items],
	);

	const deleteItemMultiple = useCallback(
		(ids: string[]) => {
			const updated = items.filter((item) => !ids.includes(item.id));
			setItems(updated);
			return updated;
		},
		[items],
	);

	return {
		items,
		addItem,
		updateItem,
		deleteItem,
		deleteItemMultiple,
	};
}

function usePaginatedTableRows<T>(tableRows: EditableListRow<T>[], pageSize: number) {
	const [page, setPage] = useState(1);
	const totalPages = Math.max(1, Math.ceil(tableRows.length / pageSize));

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	const paginatedRows = useMemo(() => {
		const startIdx = (page - 1) * pageSize;
		return tableRows.slice(startIdx, startIdx + pageSize);
	}, [page, pageSize, tableRows]);

	return { paginatedRows, page, setPage, totalPages } as const;
}

export function useTableRowActions() {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [editingId, setEditingId] = useState<string | null>(null);

	const toggleSelectOne = useCallback((id: string, selected: boolean) => {
		if (selected) {
			setSelectedIds((prev) => new Set([...prev, id]));
		} else {
			setSelectedIds((prev) => new Set([...prev].filter((i) => i !== id)));
		}
		setEditingId(null);
	}, []);

	const toggleEditOne = useCallback((id: string) => {
		setEditingId((prev) => (prev === id ? null : id));
	}, []);

	const clearEditing = useCallback(() => {
		setEditingId(null);
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	return {
		selectedIds,
		editingId,
		setEditingId,
		toggleSelectOne,
		toggleEditOne,
		clearEditing,
		clearSelection,
	};
}

type UseEditableListProps<T> = {
	initialItems: T[];
	onUpdate: (items: T[]) => void;
	pageSize?: number;
	getDisabledState?: (item: T) => boolean;
	// New: Tell the hook how to build the object
	makeItem: (value: string) => T;
};

export default function useEditableList<T>({
	initialItems,
	onUpdate,
	getDisabledState,
	pageSize = 4,
	makeItem,
}: UseEditableListProps<T>) {
	const [searchQuery, setSearchQuery] = useState<string>("");

	const { addItem, deleteItem, deleteItemMultiple, updateItem, items } = useDraftItem(initialItems);

	// 4. Derive Rows for UI
	const baseTableRows = useMemo(
		() =>
			items.map((item) => ({
				...item,
				disabled: getDisabledState?.(item) ?? false,
				selected: false,
				editing: false, // Manage editingId separately if needed
			})),
		[items, getDisabledState],
	);

	const { paginatedRows, page, setPage, totalPages } = usePaginatedTableRows(baseTableRows, pageSize);

	const { clearEditing, clearSelection, editingId, selectedIds, toggleEditOne, toggleSelectOne } = useTableRowActions();

	const handleToggleOne = useCallback(
		(id: string, isSelected: boolean) => {
			toggleSelectOne(id, isSelected);
			const nextIds = new Set(selectedIds);
			isSelected ? nextIds.add(id) : nextIds.delete(id);
		},
		[selectedIds, toggleSelectOne],
	);

	const allTableRows = useMemo((): EditableListRow<T>[] => {
		return baseTableRows.map((item) => ({
			...item,
			selected: selectedIds.has(item.id),
			editing: editingId === item.id,
		}));
	}, [baseTableRows, selectedIds, editingId]);

	const handleAdd = useCallback(
		(value: string) => {
			const trimmed = value.trim();
			if (!trimmed) return;

			addItem(makeItem(value), (item) => {
				const nextItems = [item, ...items];
				onUpdate(nextItems);
			});

			setPage(1);
			setSearchQuery("");
		},
		[addItem, items, makeItem, onUpdate, setPage],
	);

	const handleUpdateRow = useCallback(
		(id: string, updates: Partial<T>) => {
			const nextItems = updateItem(id, updates);
			onUpdate(nextItems);
			clearEditing();
		},
		[clearEditing, onUpdate, updateItem],
	);

	const handleDeleteSelected = useCallback(() => {
		const toDelete = items.filter((item) => selectedIds.has(item.id)).map((item) => item.id);

		const nextItems = deleteItemMultiple(toDelete);

		clearSelection();
		onUpdate(nextItems);
	}, [clearSelection, deleteItemMultiple, items, onUpdate, selectedIds]);

	const handleDeleteOne = useCallback(
		(id: string) => {
			toggleSelectOne(id, false); // Remove from selected list

			// Delete from list
			const nextItems = deleteItem(id);

			//  Notify parent with the updated selection
			onUpdate(nextItems);
		},
		[deleteItem, onUpdate, toggleSelectOne],
	);

	// derive final rows with selection/editing state
	const tableRows = useMemo((): EditableListRow<T>[] => {
		return paginatedRows.map((item) => ({
			...item,
			selected: selectedIds.has(item.id),
			editing: editingId === item.id,
		}));
	}, [paginatedRows, selectedIds, editingId]);

	//  Actions

	const allSelected = useMemo(() => {
		const areSomeNonDisabled = tableRows.some((x) => !(x?.disabled ?? false));
		const areAllNonDisabledSelected = tableRows.every((x) => x?.disabled || x.selected);
		return areSomeNonDisabled && areAllNonDisabledSelected;
	}, [tableRows]);

	const hasSelection = useMemo(() => {
		return tableRows.some((x) => x.selected);
	}, [tableRows]);

	const areAllDisabled = useMemo(() => {
		return tableRows.every((x) => x?.disabled ?? false);
	}, [tableRows]);

	const selectedCount = selectedIds.size;

	return {
		pagination: { page, totalPages, setPage, paginatedRows },
		rows: { baseTableRows, tableRows, allTableRows },
		tableState: { allSelected, hasSelection, areAllDisabled, selectedCount },
		tableActions: {
			toggleEditOne,
			toggleSelectOne: handleToggleOne,
			clearEditing,
			clearSelection,
			handleDeleteOne,
			handleUpdate: handleUpdateRow,
			handleDeleteSelected,
			handleAdd,
		},
		searchState: { searchQuery, setSearchQuery },
	};
}
